import type { ViewerAnnotationState } from '@/lib/annotationAdapter';

const IOU_THRESHOLD = 0.5;
const COORD_TOLERANCE_PX = 2;

export type AnnotationChangeKind = 'changed' | 'added' | 'removed';

export interface DiffableAnnotation extends ViewerAnnotationState {
  _id: string;
}

export interface AnnotationChange {
  id: string;
  kind: AnnotationChangeKind;
  original: DiffableAnnotation | null;
  current: DiffableAnnotation | null;
}

type BBox = [number, number, number, number];

function bboxOf(ann: DiffableAnnotation): BBox {
  if (!ann.points?.length) return [0, 0, 0, 0];
  if (ann.type === 'rectangle' && ann.points.length >= 2) {
    const xs = [ann.points[0].x, ann.points[1].x];
    const ys = [ann.points[0].y, ann.points[1].y];
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  }
  const xs = ann.points.map((p) => p.x);
  const ys = ann.points.map((p) => p.y);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

function iou(a: BBox, b: BBox): number {
  const ix1 = Math.max(a[0], b[0]);
  const iy1 = Math.max(a[1], b[1]);
  const ix2 = Math.min(a[2], b[2]);
  const iy2 = Math.min(a[3], b[3]);
  const iw = Math.max(0, ix2 - ix1);
  const ih = Math.max(0, iy2 - iy1);
  const inter = iw * ih;
  if (inter <= 0) return 0;
  const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
  const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
}

function coordsEqual(a: DiffableAnnotation, b: DiffableAnnotation): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'rectangle') {
    const ba = bboxOf(a);
    const bb = bboxOf(b);
    return ba.every((v, i) => Math.abs(v - bb[i]) <= COORD_TOLERANCE_PX);
  }
  if (a.points.length !== b.points.length) return false;
  return a.points.every((p, i) => {
    const q = b.points[i];
    return Math.abs(p.x - q.x) <= COORD_TOLERANCE_PX && Math.abs(p.y - q.y) <= COORD_TOLERANCE_PX;
  });
}

function bboxArea(b: BBox): number {
  return Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
}

/** IoU match score; zero-area (collinear) cells fall back to geometry equality only. */
function matchScore(a: DiffableAnnotation, b: DiffableAnnotation): number {
  const ba = bboxOf(a);
  const bb = bboxOf(b);
  const score = iou(ba, bb);
  if (score > 0) return score;
  // Degenerate masks never get IoU > 0 even when identical — pair them only
  // when both have zero area and the coordinates match. Normal cells unaffected.
  if (bboxArea(ba) === 0 && bboxArea(bb) === 0 && coordsEqual(a, b)) {
    return 1;
  }
  return 0;
}

function contentEqual(a: DiffableAnnotation, b: DiffableAnnotation): boolean {
  return (
    a.label === b.label &&
    (a.sublabel || '') === (b.sublabel || '') &&
    coordsEqual(a, b)
  );
}

function cloneAnnotation(ann: DiffableAnnotation, newId: string): DiffableAnnotation {
  return {
    ...ann,
    _id: newId,
    id: newId,
    points: ann.points.map((p) => ({ ...p })),
    attributes: ann.attributes ? { ...ann.attributes } : undefined,
  };
}

/** Pair originals ↔ current doctor annotations; return only differing cells. */
export function buildAnnotationChanges(
  originals: DiffableAnnotation[],
  current: DiffableAnnotation[],
): AnnotationChange[] {
  const usedOriginal = new Set<number>();
  const matches: { oi: number; ci: number }[] = [];

  for (let ci = 0; ci < current.length; ci++) {
    let bestOi = -1;
    let bestScore = IOU_THRESHOLD;
    for (let oi = 0; oi < originals.length; oi++) {
      if (usedOriginal.has(oi)) continue;
      const score = matchScore(originals[oi], current[ci]);
      if (score >= bestScore) {
        bestScore = score;
        bestOi = oi;
      }
    }
    if (bestOi >= 0) {
      usedOriginal.add(bestOi);
      matches.push({ oi: bestOi, ci });
    }
  }

  const matchedCurrent = new Set(matches.map((m) => m.ci));
  const changes: AnnotationChange[] = [];

  for (const { oi, ci } of matches) {
    const original = originals[oi];
    const cur = current[ci];
    if (contentEqual(original, cur)) continue;
    changes.push({
      id: `chg-${cur._id}`,
      kind: 'changed',
      original,
      current: cur,
    });
  }

  for (let ci = 0; ci < current.length; ci++) {
    if (matchedCurrent.has(ci)) continue;
    const cur = current[ci];
    changes.push({
      id: `add-${cur._id}`,
      kind: 'added',
      original: null,
      current: cur,
    });
  }

  for (let oi = 0; oi < originals.length; oi++) {
    if (usedOriginal.has(oi)) continue;
    const original = originals[oi];
    changes.push({
      id: `rm-${original._id}`,
      kind: 'removed',
      original,
      current: null,
    });
  }

  return changes;
}

export function formatAnnCaption(ann: DiffableAnnotation | null): string {
  if (!ann) return '—';
  const label = ann.label || 'Unlabeled';
  return ann.sublabel ? `${label} · ${ann.sublabel}` : label;
}

export function revertChangeInList(
  annotations: DiffableAnnotation[],
  change: AnnotationChange,
): DiffableAnnotation[] {
  if (change.kind === 'added' && change.current) {
    return annotations.filter((a) => a._id !== change.current!._id);
  }
  if (change.kind === 'removed' && change.original) {
    const restored = cloneAnnotation(change.original, `restored-${change.original._id}-${Date.now()}`);
    return [...annotations, restored];
  }
  if (change.kind === 'changed' && change.original && change.current) {
    const restored = cloneAnnotation(change.original, change.current._id);
    return annotations.map((a) => (a._id === change.current!._id ? restored : a));
  }
  return annotations;
}
